import React, { useState, useEffect, useCallback } from 'react';
import { clickable } from '../../../utils/a11y';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaLayerGroup, FaArrowLeft, FaPlus, FaEdit, FaTrash, FaCheckCircle,
  FaClock, FaMapPin, FaBuilding, FaFire, FaWrench, FaShieldAlt,
  FaBars, FaBell, FaUserCircle, FaHistory, FaSignOutAlt, FaServer,
  FaChevronRight, FaExclamationTriangle, FaTimes, FaCheckSquare, FaSquare
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getApiEndpoint } from '../../../config/api';
import { getUserRole } from '../../../utils/roleUtils';
import { getAuthHeader } from '../../../utils/auth';
import CloudStaffSidebar from './CloudStaffSidebar';
import cloudsafeLogo from '../../../assets/cloudsafe-logo.png';
import './AssetGenerationPropertyPage.css';

const MODULE_OPTIONS = [
  { key: 'roof_safety',      label: 'Roof Safety',      icon: FaShieldAlt, color: '#38bdf8' },
  { key: 'fire_safety',      label: 'Fire Safety',      icon: FaFire,      color: '#f87171' },
  { key: 'equipment_safety', label: 'Equipment Safety', icon: FaWrench,    color: '#fbbf24' },
];

function moduleInfo(key) {
  return MODULE_OPTIONS.find(m => m.key === key) || { label: key, icon: FaLayerGroup, color: '#94a3b8' };
}

function timeAgo(ts) {
  if (!ts) return '—';
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(ts).toLocaleDateString();
}

export default function AssetGenerationPropertyPage() {
  const { id: propertyId } = useParams();
  const navigate = useNavigate();

  const [property, setProperty] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userName, setUserName] = useState('');

  // New session modal
  const [showNewModal, setShowNewModal] = useState(false);
  const [newModuleKey, setNewModuleKey] = useState('roof_safety');
  const [newTitle, setNewTitle] = useState('');
  const [newLevelName, setNewLevelName] = useState('');
  const [creating, setCreating] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null); // session object
  const [deleting, setDeleting] = useState(false);

  // Multi-select
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    const role = getUserRole();
    if (role !== 'cloud_staff' && role !== 'super_admin') {
      toast.error('Access denied');
      navigate('/login');
      return;
    }
    loadData();
  }, [propertyId]);

  const loadData = useCallback(async () => {
    setLoading(true);
    const headers = getAuthHeader();
    try {
      const [propRes, sessRes] = await Promise.all([
        fetch(getApiEndpoint(`/api/properties/${propertyId}`), { headers }),
        fetch(getApiEndpoint(`/api/onboarding-wizard/properties/${propertyId}/sessions`), { headers }),
      ]);
      if (propRes.ok) setProperty(await propRes.json());
      if (sessRes.ok) {
        const d = await sessRes.json();
        setSessions(d.sessions || []);
      }
    } catch (e) {
      toast.error('Failed to load property data');
    } finally {
      setLoading(false);
    }
    // Load user name
    try {
      const r = await fetch(getApiEndpoint('/api/users/profile'), { headers });
      if (r.ok) {
        const d = await r.json();
        setUserName(`${d.firstName || d.first_name || ''} ${d.lastName || d.last_name || ''}`.trim());
      }
    } catch {}
  }, [propertyId]);

  const handleCreateSession = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await fetch(
        getApiEndpoint(`/api/onboarding-wizard/properties/${propertyId}/sessions`),
        {
          method: 'POST',
          headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            module_key: newModuleKey,
            title: newTitle.trim() || null,
            level_name: newLevelName.trim() || null,
          }),
        }
      );
      if (!res.ok) throw new Error('Failed to create session');
      const data = await res.json();
      toast.success('Onboarding session created');
      setShowNewModal(false);
      navigate(`/cloud-staff/asset-generation-v2/${propertyId}/${data.session?.id || data.id}`);
    } catch {
      toast.error('Failed to create session');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(
        getApiEndpoint(`/api/onboarding-wizard/sessions/${deleteTarget.id}`),
        { method: 'DELETE', headers: getAuthHeader() }
      );
      if (!res.ok) throw new Error();
      toast.success('Session deleted');
      setSessions(prev => prev.filter(s => s.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch {
      toast.error('Failed to delete session');
    } finally {
      setDeleting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    navigate('/login');
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (group) => {
    const groupIds = group.map(s => s.id);
    const allSelected = groupIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) groupIds.forEach(id => next.delete(id));
      else groupIds.forEach(id => next.add(id));
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    const ids = [...selectedIds];
    try {
      await Promise.all(ids.map(id =>
        fetch(getApiEndpoint(`/api/onboarding-wizard/sessions/${id}`), {
          method: 'DELETE',
          headers: getAuthHeader(),
        })
      ));
      setSessions(prev => prev.filter(s => !selectedIds.has(s.id)));
      setSelectedIds(new Set());
      setShowBulkDeleteConfirm(false);
      toast.success(`Deleted ${ids.length} session${ids.length !== 1 ? 's' : ''}`);
    } catch {
      toast.error('Some sessions could not be deleted');
    } finally {
      setBulkDeleting(false);
    }
  };

  const drafts = sessions.filter(s => s.status === 'draft');
  const confirmed = sessions.filter(s => s.status === 'confirmed');

  const renderSession = (session) => {
    const mod = moduleInfo(session.module_key);
    const ModIcon = mod.icon;
    const isConfirmed = session.status === 'confirmed';
    const stepOf = session.current_step ? `Step ${session.current_step}/5` : null;
    const isSelected = selectedIds.has(session.id);

    return (
      <div key={session.id} className={`agpp-session-row${isSelected ? ' agpp-selected' : ''}`}>
        <button
          className="agpp-select-cb"
          onClick={e => { e.stopPropagation(); toggleSelect(session.id); }}
          title={isSelected ? 'Deselect' : 'Select'}
        >
          {isSelected ? <FaCheckSquare className="agpp-cb-checked" /> : <FaSquare className="agpp-cb-empty" />}
        </button>
        <div className="agpp-session-mod" style={{ background: mod.color + '18', borderColor: mod.color + '40' }}>
          <ModIcon style={{ color: mod.color }} />
        </div>
        <div className="agpp-session-info">
          <div className="agpp-session-name">
            {session.title || mod.label}
            {session.level_name && <span className="agpp-session-level">{session.level_name}</span>}
          </div>
          <div className="agpp-session-meta">
            <span className={`agpp-session-status ${isConfirmed ? 'confirmed' : 'draft'}`}>
              {isConfirmed ? <FaCheckCircle /> : <FaClock />}
              {isConfirmed ? 'Confirmed' : `Draft${stepOf ? ` · ${stepOf}` : ''}`}
            </span>
            <span className="agpp-session-pins"><FaMapPin /> {session.pin_count || 0} pins</span>
            <span className="agpp-session-date">{timeAgo(session.last_edited_at || session.created_at)}</span>
            {session.last_edited_by_name && (
              <span className="agpp-session-author">by {session.last_edited_by_name}</span>
            )}
          </div>
        </div>
        <div className="agpp-session-actions">
          {!isConfirmed && (
            <button
              className="agpp-action-btn agpp-action-edit"
              title="Continue editing"
              onClick={() => navigate(`/cloud-staff/asset-generation-v2/${propertyId}/${session.id}`)}
            >
              <FaEdit /> <span>Edit</span>
            </button>
          )}
          {isConfirmed && (
            <button
              className="agpp-action-btn agpp-action-view"
              title="View session"
              onClick={() => navigate(`/cloud-staff/asset-generation-v2/${propertyId}/${session.id}`)}
            >
              <FaChevronRight /> <span>View</span>
            </button>
          )}
          <button
            className="agpp-action-btn agpp-action-delete"
            title="Delete session"
            onClick={() => setDeleteTarget(session)}
          >
            <FaTrash />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="cs-dashboard">
      <header className="cs-header">
        <div className="cs-header-left">
          <button className="cs-mobile-menu-btn" onClick={() => setMobileSidebarOpen(true)} aria-label="Open menu">
            <FaBars />
          </button>
          <img src={cloudsafeLogo} alt="CloudSafe" className="cs-logo" />
          <div className="cs-header-badge"><FaServer /><span>Cloud Staff Portal</span></div>
        </div>
        <div className="cs-header-right">
          <div className="cs-header-icons">
            <button className="cs-icon-btn" title="Notifications"><FaBell /></button>
            <div className="cs-user-avatar" aria-haspopup="menu" aria-expanded={showUserMenu}
                 {...clickable(() => setShowUserMenu(!showUserMenu), { label: 'User menu' })}>
              <FaUserCircle />
              {showUserMenu && (
                <div className="cs-user-menu">
                  <div className="cs-user-menu-header">
                    <FaUserCircle className="cs-menu-avatar" />
                    <div>
                      <p className="cs-menu-name">{userName || 'Cloud Staff'}</p>
                      <p className="cs-menu-role">Cloud Staff Admin</p>
                    </div>
                  </div>
                  <div className="cs-user-menu-divider" />
                  <button onClick={() => navigate('/cloud-staff/activity-log')} className="cs-menu-item"><FaHistory /> Activity Log</button>
                  <button onClick={handleLogout} className="cs-menu-item cs-menu-logout"><FaSignOutAlt /> Sign Out</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="cs-layout">
        <CloudStaffSidebar
          active="asset-generation-v2"
          mobileOpen={mobileSidebarOpen}
          onClose={() => setMobileSidebarOpen(false)}
        />

        <main className="cs-content">
          {/* Back + page header */}
          <div className="agpp-topbar">
            <button className="agpp-back-btn" onClick={() => navigate('/cloud-staff/asset-generation-v2')}>
              <FaArrowLeft /> All Properties
            </button>
          </div>

          <div className="agpp-property-header">
            <div className="agpp-property-icon"><FaBuilding /></div>
            <div className="agpp-property-info">
              <h1 className="agpp-property-name">{loading ? '—' : (property?.name || 'Property')}</h1>
              <p className="agpp-property-address">{property?.address || ''}</p>
            </div>
            <button className="agpp-new-btn" onClick={() => setShowNewModal(true)}>
              <FaPlus /> New Onboarding
            </button>
          </div>

          {/* Stats strip */}
          {!loading && (
            <div className="agpp-stats">
              <div className="agpp-stat">
                <span className="agpp-stat-value">{sessions.length}</span>
                <span className="agpp-stat-label">Total Sessions</span>
              </div>
              <div className="agpp-stat">
                <span className="agpp-stat-value agpp-stat-draft">{drafts.length}</span>
                <span className="agpp-stat-label">In Progress</span>
              </div>
              <div className="agpp-stat">
                <span className="agpp-stat-value agpp-stat-confirmed">{confirmed.length}</span>
                <span className="agpp-stat-label">Confirmed</span>
              </div>
              <div className="agpp-stat">
                <span className="agpp-stat-value">{sessions.reduce((t, s) => t + (s.pin_count || 0), 0)}</span>
                <span className="agpp-stat-label">Total Pins</span>
              </div>
            </div>
          )}

          {loading ? (
            <div className="agpp-loading"><div className="cs-spinner" /><p>Loading sessions…</p></div>
          ) : sessions.length === 0 ? (
            <div className="agpp-empty">
              <FaLayerGroup className="agpp-empty-icon" />
              <h3>No onboarding sessions yet</h3>
              <p>Start a new onboarding session to upload a floor plan, drop pins, and generate asset IDs for this property.</p>
              <button className="agpp-new-btn" onClick={() => setShowNewModal(true)}>
                <FaPlus /> Start First Session
              </button>
            </div>
          ) : (
            <div className="agpp-sessions">
              {drafts.length > 0 && (
                <section className="agpp-section">
                  <div className="agpp-section-header">
                    <FaClock className="agpp-section-icon agpp-section-icon-draft" />
                    <span>In Progress</span>
                    <span className="agpp-section-count">{drafts.length}</span>
                    <button className="agpp-select-all-btn" onClick={() => toggleSelectAll(drafts)}>
                      {drafts.every(s => selectedIds.has(s.id)) ? 'Deselect all' : 'Select all'}
                    </button>
                  </div>
                  <div className="agpp-session-list">
                    {drafts.map(renderSession)}
                  </div>
                </section>
              )}

              {confirmed.length > 0 && (
                <section className="agpp-section">
                  <div className="agpp-section-header">
                    <FaCheckCircle className="agpp-section-icon agpp-section-icon-confirmed" />
                    <span>Confirmed</span>
                    <span className="agpp-section-count">{confirmed.length}</span>
                    <button className="agpp-select-all-btn" onClick={() => toggleSelectAll(confirmed)}>
                      {confirmed.every(s => selectedIds.has(s.id)) ? 'Deselect all' : 'Select all'}
                    </button>
                  </div>
                  <div className="agpp-session-list">
                    {confirmed.map(renderSession)}
                  </div>
                </section>
              )}
            </div>
          )}
        </main>
      </div>

      {/* ── New Session Modal ── */}
      {showNewModal && (
        <div className="agpp-modal-overlay" onClick={() => setShowNewModal(false)}>
          <div className="agpp-modal" onClick={e => e.stopPropagation()}>
            <div className="agpp-modal-header">
              <h2><FaPlus /> New Onboarding Session</h2>
              <button className="agpp-modal-close" onClick={() => setShowNewModal(false)}><FaTimes /></button>
            </div>
            <form onSubmit={handleCreateSession} className="agpp-modal-body">
              <div className="agpp-modal-field">
                <label>Module</label>
                <div className="agpp-module-grid">
                  {MODULE_OPTIONS.map(mod => {
                    const ModIcon = mod.icon;
                    return (
                      <button
                        key={mod.key}
                        type="button"
                        className={`agpp-module-option ${newModuleKey === mod.key ? 'selected' : ''}`}
                        style={newModuleKey === mod.key ? { borderColor: mod.color, background: mod.color + '14' } : {}}
                        onClick={() => setNewModuleKey(mod.key)}
                      >
                        <ModIcon style={{ color: mod.color }} />
                        <span>{mod.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="agpp-modal-field">
                <label>Session Title <span className="agpp-optional">(optional)</span></label>
                <input
                  type="text"
                  className="agpp-modal-input"
                  placeholder={`e.g. ${moduleInfo(newModuleKey).label} — North Wing`}
                  value={newTitle}
                  onChange={e => setNewTitle(e.target.value)}
                />
              </div>
              <div className="agpp-modal-field">
                <label>Floor / Level <span className="agpp-optional">(optional)</span></label>
                <input
                  type="text"
                  className="agpp-modal-input"
                  placeholder="e.g. Level 3, Ground Floor, Roof"
                  value={newLevelName}
                  onChange={e => setNewLevelName(e.target.value)}
                />
              </div>
              <div className="agpp-modal-footer">
                <button type="button" className="agpp-btn-secondary" onClick={() => setShowNewModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="agpp-btn-primary" disabled={creating}>
                  {creating ? 'Creating…' : <><FaPlus /> Start Onboarding</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Bulk Action Bar ── */}
      {selectedIds.size > 0 && (
        <div className="agpp-bulk-bar">
          <span className="agpp-bulk-count">{selectedIds.size} selected</span>
          <button className="agpp-bulk-btn agpp-bulk-delete" onClick={() => setShowBulkDeleteConfirm(true)}>
            <FaTrash /> Delete Selected
          </button>
          <button className="agpp-bulk-btn agpp-bulk-cancel" onClick={clearSelection}>
            <FaTimes /> Clear
          </button>
        </div>
      )}

      {/* ── Bulk Delete Confirm Modal ── */}
      {showBulkDeleteConfirm && (
        <div className="agpp-modal-overlay" onClick={() => setShowBulkDeleteConfirm(false)}>
          <div className="agpp-modal agpp-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="agpp-modal-header">
              <h2><FaExclamationTriangle className="agpp-warn-icon" /> Delete {selectedIds.size} Session{selectedIds.size !== 1 ? 's' : ''}</h2>
              <button className="agpp-modal-close" onClick={() => setShowBulkDeleteConfirm(false)}><FaTimes /></button>
            </div>
            <div className="agpp-modal-body">
              <p className="agpp-delete-msg">
                This will permanently delete <strong>{selectedIds.size} session{selectedIds.size !== 1 ? 's' : ''}</strong> and all their pins. This cannot be undone.
              </p>
              <div className="agpp-modal-footer">
                <button className="agpp-btn-secondary" onClick={() => setShowBulkDeleteConfirm(false)}>Cancel</button>
                <button className="agpp-btn-danger" onClick={handleBulkDelete} disabled={bulkDeleting}>
                  {bulkDeleting ? 'Deleting…' : <><FaTrash /> Delete All</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ── */}
      {deleteTarget && (
        <div className="agpp-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="agpp-modal agpp-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="agpp-modal-header">
              <h2><FaExclamationTriangle className="agpp-warn-icon" /> Delete Session</h2>
              <button className="agpp-modal-close" onClick={() => setDeleteTarget(null)}><FaTimes /></button>
            </div>
            <div className="agpp-modal-body">
              <p className="agpp-delete-msg">
                This will permanently delete <strong>{deleteTarget.title || moduleInfo(deleteTarget.module_key).label}</strong> and all its pins. This cannot be undone.
              </p>
              <div className="agpp-modal-footer">
                <button className="agpp-btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
                <button className="agpp-btn-danger" onClick={handleDelete} disabled={deleting}>
                  {deleting ? 'Deleting…' : <><FaTrash /> Delete Session</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
